import type { PageHelp } from "../../help/types";
import {
  DOCUMENTS_CONTEXTS,
  DOCUMENTS_DIALOG,
  LIST_VIEW_CONTEXT,
  PACKAGE_DIALOG_DETAILS,
  PACKAGE_DIALOG_RULES,
  documentsDialogSteps,
  packageDialogSteps,
} from "../CompanyPackagesPage/help-steps";
import {
  IMPLEMENTATION_CONSENT_CONTEXTS,
  IMPLEMENTATION_DIALOG_BENEFIT,
  IMPLEMENTATION_DIALOG_POLICY,
  implementationDialogSteps,
} from "./help-steps";

const GETTING_AROUND = "Getting around";
const TOOLBAR = "Toolbar";
const READING_CARDS = "Reading the implementation cards";
const CARD_ACTIONS = "Working with an implementation card";
const LIST_VIEW = "List view";

export const companyBenefitImplementationPageHelp: PageHelp = {
  title: "Benefit implementations",
  intro:
    "The implementations of one benefit category for a client: the concrete insurer, policy, validity period, currency and consent rules behind the benefit. Each implementation holds the packages employees enroll in. You reach this page from a category card on the Company profile.",
  sections: [
    GETTING_AROUND,
    TOOLBAR,
    READING_CARDS,
    CARD_ACTIONS,
    LIST_VIEW,
    IMPLEMENTATION_DIALOG_BENEFIT,
    IMPLEMENTATION_DIALOG_POLICY,
    PACKAGE_DIALOG_DETAILS,
    PACKAGE_DIALOG_RULES,
    DOCUMENTS_DIALOG,
  ],
  contexts: {
    "list-view": LIST_VIEW_CONTEXT,
    "implementation-dialog":
      'Click "Add Benefit Implementation" in the toolbar, or the edit icon on an implementation card or table row.',
    "implementation-dialog-add": 'Click "Add Benefit Implementation" in the toolbar.',
    "implementation-dialog-edit":
      "Click the edit icon on an implementation card or table row.",
    ...IMPLEMENTATION_CONSENT_CONTEXTS,
    "package-dialog":
      'Click the + on an implementation card ("Add New Package"), or "Add Package" in a table row.',
    "package-dialog-add":
      'Click the + on an implementation card ("Add New Package"), or "Add Package" in a table row.',
    "documents-dialog":
      'Click the documents icon on an implementation card, or "Manage Documents" / "Add Documents" in a table row or in the implementation dialog.',
    ...DOCUMENTS_CONTEXTS,
  },
  tips: [
    "A category can have several implementations, for example one per insurer or one per policy year. Two implementations of the same category may not share a name or a policy number while their validity periods overlap.",
    "Editing an implementation does not overwrite it: the server keeps the previous version and stores a new one.",
    "Grid view (cards) is the default. Switch to list view in the settings menu (gear icon in the header) to edit several implementations in a table.",
    "Read-only roles see the cards without the action footer; they can still open the packages by double-clicking a card picture.",
  ],
  steps: [
    {
      selector: '[data-help="implementation-breadcrumb"]',
      section: GETTING_AROUND,
      title: "Go back to the company",
      body: "The breadcrumb shows country, company and category. Click the company to return to its benefit categories, or the country to start over with another company.",
    },
    {
      selector: '[data-help="implementation-refresh"]',
      section: GETTING_AROUND,
      title: "Reload the implementations",
      body: "Fetches the implementations of this category again from the server, for example after a colleague changed them.",
    },
    {
      selector: '[data-help="implementation-search"]',
      section: GETTING_AROUND,
      title: "Search implementations by name",
      body: "Filters the cards or table rows to implementations whose name contains the text. The number at the right end of the bar counts all implementations loaded, not only the matches. Disabled when there are none.",
    },
    {
      selector: '[data-help="only-active-switch"]',
      section: GETTING_AROUND,
      title: "Show only implementations valid today",
      body: '"Only Active" hides implementations whose validity period has not started yet or has already ended. Switch it off to see past and future implementations as well.',
    },
    {
      selector: '[data-help="benefit-cards-save-order"]',
      section: TOOLBAR,
      title: "Save your card order",
      body: 'In grid view you can drag the implementation cards into any order; "Save Order" stores it for your user only. Disabled while a search is active.',
    },
    {
      selector: '[data-help="implementation-add"]',
      section: TOOLBAR,
      title: "Add an implementation to the category",
      body: '"Add Benefit Implementation" opens the implementation dialog for this category. Only admin roles (global, country or company admin) can use it; for read-only roles it is disabled.',
    },
    {
      selector: '[data-help="benefit-card"]',
      section: READING_CARDS,
      optional: true,
      title: "Read an implementation card",
      body: "Each card is one implementation: picture, name and description (hover to read it in full). Double-click the picture to open its packages. Drag a card to reorder the list, then save the order in the toolbar.",
    },
    {
      selector: '[data-help="benefit-card-tags"]',
      section: READING_CARDS,
      optional: true,
      title: "See the packages and the validity",
      body: "The gift tag shows how many packages the implementation has; the calendar tag shows its validity period from Valid From to Valid To.",
    },
    {
      selector: '[data-help="benefit-card-open"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Open the packages of an implementation",
      body: '"Open Packages" goes to the packages page of this implementation, where prices, payment split and eligibility are set.',
    },
    {
      selector: '[data-help="benefit-card-add"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Add a package from the card",
      body: 'The + on the card picture ("Add New Package") opens the package dialog for this implementation without leaving the page.',
    },
    {
      selector: '[data-help="benefit-card-documents"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Manage the documents of an implementation",
      body: "Opens the documents dialog of this implementation, for example for policy terms. Changes there are saved immediately.",
    },
    {
      selector: '[data-help="benefit-card-edit"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "admin",
      title: "Edit an implementation",
      body: "Opens the implementation dialog with the current values: consent rules, currency, policy and validity.",
    },
    {
      selector: '[data-help="benefit-card-delete"]',
      section: CARD_ACTIONS,
      optional: true,
      minRole: "superadmin",
      title: "Delete an implementation",
      body: "Deletes the implementation after you confirm, together with its documents and all its packages and their employee enrollments. The server carries out deletes only for GrECo global administrators.",
    },
    {
      selector: '[data-help="pagination"]',
      section: CARD_ACTIONS,
      optional: true,
      title: "Page through many implementations",
      body: "Appears above the cards when there are more than 50 implementations. Type a page number and press Enter, or use the arrows and page buttons.",
    },
    {
      selector: '[data-help="implementation-table"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Edit implementations in the table",
      body: "List view shows one row per implementation with its consent, policy, validity, currency and package count. Admin roles can change several cells directly; the row then shows Save and Undo in the Actions column. Nothing is stored until you click Save.",
    },
    {
      selector: '[data-help="implementation-row-add-package"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Add a package from a row",
      body: '"Add Package" opens the package dialog for the implementation in that row. Disabled for read-only roles.',
    },
    {
      selector: '[data-help="implementation-row-documents"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Open the documents of a row",
      body: 'The Documents cell shows "Manage Documents" with the number of files, or "Add Documents" when there are none, and opens the documents dialog. Disabled for read-only roles.',
    },
    {
      selector: '[data-help="implementation-row-edit"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Edit a row in the dialog",
      body: "The pencil icon opens the full implementation dialog for this row. Disabled for read-only roles.",
    },
    {
      selector: '[data-help="implementation-row-open"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Open the packages of a row",
      body: "The open icon goes to the packages page of this implementation. It works for every role, read-only included.",
    },
    {
      selector: '[data-help="implementation-row-save"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Save changes made in a row",
      body: "Appears after you change a cell. Sends the row to the server; if a rule is broken (for example Valid To not after Valid From, or an overlapping policy number) the server refuses and the row keeps your changes.",
    },
    {
      selector: '[data-help="implementation-row-undo"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Undo changes in a row",
      body: "Puts the row back to its last saved values without contacting the server. Appears next to Save once a cell has been changed.",
    },
    {
      selector: '[data-help="implementation-row-delete"]',
      section: LIST_VIEW,
      context: "list-view",
      optional: true,
      title: "Delete an implementation from the table",
      body: "Deletes the implementation after you confirm, with its documents, packages and their enrollments. The icon is active for admin roles, but the server carries out deletes only for GrECo global administrators.",
    },
    ...implementationDialogSteps,
    // Packages can only be ADDED from this page, so the edit-only step is left out.
    ...packageDialogSteps.filter((step) => step.context !== "package-dialog-edit"),
    ...documentsDialogSteps,
  ],
};
